import { Text, View } from '@react-pdf/renderer';

import type { Scan } from '../../../../api/generated/insights.schemas';
import { palette, styles } from '../styles';

interface CitationsSectionProps {
  scan: Scan;
}

export default function CitationsSection({ scan }: CitationsSectionProps) {
  const list = scan.citations?.list ?? [];
  const total = scan.citations?.total?.value;

  if (list.length === 0) {
    return (
      <View>
        <Text style={styles.sectionTitle}>Citations</Text>
        <Text style={styles.sectionSubtitle}>
          The selected AI platform returned no citations for the queries.
        </Text>
      </View>
    );
  }

  return (
    <View>
      <Text style={styles.sectionTitle}>Citations</Text>
      <Text style={styles.sectionSubtitle}>
        Sources the AI used to support brand mentions, ordered by relevance.
        Brand citations are marked with ★.
      </Text>
      <Text style={styles.paragraph}>
        <Text style={{ fontFamily: 'Helvetica-Bold' }}>
          {typeof total === 'number' ? total : list.length}
        </Text>
        {' citations across the analysed queries.'}
      </Text>

      <View style={styles.table}>
        <View style={styles.tableHeader} fixed>
          <Text style={[styles.tableHeaderCell, { width: 30 }]}>#</Text>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>URL</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Domain</Text>
          <Text style={[styles.tableHeaderCell, { width: 80 }]}>Category</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Brands</Text>
        </View>

        {list.map((row, idx) => {
          const isLast = idx === list.length - 1;
          const associated = row.associatedBrands ?? [];
          const brandsLabel = associated.length
            ? associated
                .map((b) => (b.isSelf ? `${b.name} (you)` : b.name))
                .join(', ')
            : '—';
          return (
            <View
              key={`${row.url}-${idx}`}
              style={[styles.tableRow, isLast ? styles.tableRowLast : {}]}
              wrap={false}
            >
              <Text style={[styles.tableCell, { width: 30 }]}>
                {row.isBrand ? `★ ${idx + 1}` : `${idx + 1}`}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  { flex: 2, color: palette.primary },
                ]}
              >
                {row.url ?? '—'}
              </Text>
              <Text style={[styles.tableCell, { flex: 1 }]}>
                {row.rootDomain ?? '—'}
              </Text>
              <Text style={[styles.tableCell, { width: 80 }]}>
                {row.category ?? '—'}
              </Text>
              <Text style={[styles.tableCell, { flex: 1 }]}>{brandsLabel}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}
